import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`
});

function ImportExport({ onImportSuccess, dateFrom, dateTo }) {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Sadece Excel dosyaları (.xlsx, .xls) kabul edilir');
      return;
    }

    setImporting(true);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1]; 
      
      try {
        const response = await fetch(`${API_URL}/import-export/orders/import`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            file_base64: base64,
            filename: file.name
          })
        });

        const data = await response.json();

        if (response.ok) {
          setImportResult(data);
          if (data.imported > 0) {
            toast.success(`${data.imported} sipariş başarıyla import edildi!`);
            if (onImportSuccess) onImportSuccess();
          } else {
            toast.error('Hiç sipariş import edilemedi');
          }
        } else {
          toast.error(data.detail || 'Import hatası');
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Sunucuya bağlanılamadı');
      } finally {
        setImporting(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    
    reader.readAsDataURL(file);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/import-export/orders/template`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'siparis_sablonu.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Şablon indirildi');
      } else {
        toast.error('Şablon indirilemedi');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Şablon indirilemedi');
    }
  };

  const handleExportPayments = async () => {
    setExporting(true);

    try {
      let url = `${API_URL}/import-export/payments/export`;
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `odeme_kayitlari_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Ödeme kayıtları indirildi');
      } else {
        toast.error('Export hatası');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Export hatası');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="import-export-buttons">
        <button 
          className="btn-import" 
          onClick={() => setShowImportModal(true)}
          title="Excel'den Sipariş Import Et"
        >
          📤 Excel Import
        </button>
        <button 
          className="btn-export" 
          onClick={handleExportPayments}
          disabled={exporting}
          title={dateFrom || dateTo ? `Ödemeleri İndir (${dateFrom || '∞'} - ${dateTo || '∞'})` : 'Tüm Ödemeleri İndir'}
        >
          {exporting ? '⏳...' : '📥 Ödemeleri İndir'}
        </button>
      </div>

      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📤 Excel'den Sipariş Import Et</h3>
              <button className="close-btn" onClick={() => setShowImportModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="import-info">
                <h4>📋 Beklenen Kolonlar:</h4>
                <ul>
                  <li><strong>Tarih</strong> - Sipariş tarihi (örn: 2025-01-15 veya 15.01.2025)</li>
                  <li><strong>Siparişi Alan</strong> - Temsilci adı <span style={{color:'#4caf50', fontSize:'12px'}}>(otomatik listeye eklenir)</span></li>
                  <li><strong>Müşteri Adı</strong> - <span style={{color:'#e53935'}}>Zorunlu</span></li>
                  <li><strong>İş Türü</strong> - Tabela, Totem vb.</li>
                  <li><strong>Açıklama</strong> - Detaylar</li>
                  <li><strong>Fiyat</strong> - Toplam tutar</li>
                  <li><strong>Fatura</strong> - Evet/Hayır</li>
                  <li><strong>Fatura No</strong> - Fatura numarası (isteğe bağlı)</li>
                  <li><strong>Kapora</strong> - Alınan ön ödeme</li>
                  <li><strong>Kalan</strong> - Kalan borç (boş bırakılırsa otomatik hesaplanır)</li>
                </ul>
              </div>

              <div className="import-actions">
                <button className="btn-secondary" onClick={handleDownloadTemplate}>
                  📄 Örnek Şablon İndir
                </button>

                <div className="file-upload">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <button 
                    className="btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                  >
                    {importing ? '⏳ Import Ediliyor...' : '📂 Excel Dosyası Seç'}
                  </button>
                </div>
              </div>

              {importResult && (
                <div className={`import-result ${importResult.imported > 0 ? 'success' : 'error'}`}>
                  <h4>Import Sonucu:</h4>
                  <p>📊 Toplam Satır: <strong>{importResult.total_rows}</strong></p>
                  <p>✅ Import Edilen: <strong>{importResult.imported}</strong></p>
                  <p>⏭️ Atlanan: <strong>{importResult.skipped}</strong></p>
                  
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="import-errors">
                      <h5>⚠️ Hatalar:</h5>
                      <ul>
                        {importResult.errors.slice(0, 10).map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                        {importResult.errors.length > 10 && (
                          <li>... ve {importResult.errors.length - 10} hata daha</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .import-export-buttons {
          display: flex;
          gap: 8px;
        }

        .btn-import, .btn-export {
          padding: 10px 18px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .btn-import {
          background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
          color: white;
        }

        .btn-import:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
        }

        .btn-export {
          background: linear-gradient(135deg, #2196f3 0%, #1565c0 100%);
          color: white;
        }

        .btn-export:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
        }

        .btn-export:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .import-modal {
          max-width: 550px;
          width: 90%;
        }

        .import-info {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 10px;
          margin-bottom: 20px;
        }

        .import-info h4 {
          margin: 0 0 15px 0;
          color: #333;
        }

        .import-info ul {
          margin: 0;
          padding-left: 20px;
        }

        .import-info li {
          margin: 8px 0;
          color: #555;
          font-size: 14px;
        }

        .import-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin: 25px 0;
        }

        .import-actions .btn-secondary,
        .import-actions .btn-primary {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .import-actions .btn-secondary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .import-actions .btn-secondary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .import-actions .btn-primary {
          background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
          color: white;
        }

        .import-actions .btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
        }

        .import-actions .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .import-result {
          margin-top: 20px;
          padding: 20px;
          border-radius: 10px;
        }

        .import-result.success {
          background: #e8f5e9;
          border: 1px solid #4caf50;
        }

        .import-result.error {
          background: #ffebee;
          border: 1px solid #f44336;
        }

        .import-result h4 {
          margin: 0 0 15px 0;
        }

        .import-result p {
          margin: 5px 0;
        }

        .import-errors {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid rgba(0,0,0,0.1);
        }

        .import-errors h5 {
          margin: 0 0 10px 0;
          color: #f44336;
        }

        .import-errors ul {
          margin: 0;
          padding-left: 20px;
          font-size: 13px;
          color: #666;
        }

        .import-errors li {
          margin: 5px 0;
        }
      `}</style>
    </>
  );
}

export default ImportExport;